// src/components/congressional/InsightsTab.jsx
// Congressional Trade Tracker — Tab 4 (Insights).
// Design doc: docs/Design Document/CONGRESSIONAL-PAGE-REDESIGN-DESIGN.md,
// Section 3/Phase 1, item 10. Its own tab rather than a panel inside Trade
// Feed (per the reasoning already discussed: Trade Feed is a browsing-a-feed
// mode, Insights is a browsing-aggregates mode — different enough to earn
// its own tab rather than crowding Trade Feed further).
//
// THIS PASS (owner request — one consistent filter bar across every tab):
// the standalone window Select is replaced with the shared ConFilterBar
// (window + the new active/inactive/all status filter + page size), same
// component Trade Feed/Member Profiles/Committee View now use. Defaults:
// window=ytd, status=active, rows=10 — all three previously had different
// defaults (window was 90d, status wasn't filterable at all, and each
// widget had its own hardcoded limit: 15/10/10/15). Now one rowLimit value
// drives all four widgets uniformly.
//
// Renders congressInsights.js's four endpoints: top traded tickers, top
// buy/sell volume, filing lag (overall + Leaderboard), and Bipartisan
// Convergence. Convergence keeps its OWN separate day-window control
// (a different kind of "window" — days between two trades, not a lookback
// period — conflating the two would be confusing, kept separate
// deliberately, unchanged from before).
//
// Convergence rows here are labeled EXACTLY the same way as the Trade Feed's
// own badge — "cluster context," never "signal" — see design doc Section 8.

import { useState, useEffect, useCallback } from 'react';
import { TrendingUp, ArrowLeftRight } from 'lucide-react';
import { API_URL } from '../../styles/shared';
import Select from '../ui/Select';
import ConFilterBar from './ConFilterBar';
import { PARTY_COLORS, fmtDate, Card, authHeaders } from './congressShared';

function fmtUSD(n) {
  if (n == null) return '—';
  return '$' + Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });
}

export default function InsightsTab({ token }) {
  const [dateWindow, setDateWindow] = useState('ytd');
  const [status, setStatus] = useState('active');
  const [rowLimit, setRowLimit] = useState(10);
  const [convergenceDays, setConvergenceDays] = useState('14');

  const [topTickers, setTopTickers] = useState([]);
  const [topVolume, setTopVolume] = useState([]);
  const [filingLagOverall, setFilingLagOverall] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [convergence, setConvergence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const headers = authHeaders(token);
      const common = `window=${dateWindow}&status=${status}&limit=${rowLimit}`;
      const [tickersR, volumeR, lagOverallR, leaderboardR, convergenceR] = await Promise.all([
        fetch(`${API_URL}/api/congress/insights/top-tickers?${common}`, { headers }),
        fetch(`${API_URL}/api/congress/insights/top-volume?${common}`, { headers }),
        fetch(`${API_URL}/api/congress/insights/filing-lag?groupBy=overall&window=${dateWindow}&status=${status}`, { headers }),
        fetch(`${API_URL}/api/congress/insights/filing-lag?groupBy=member&window=${dateWindow}&status=${status}&limit=${rowLimit}`, { headers }),
        fetch(`${API_URL}/api/congress/insights/convergence?window=${convergenceDays}&status=${status}&limit=${rowLimit}`, { headers }),
      ]);
      const [tickersD, volumeD, lagOverallD, leaderboardD, convergenceD] = await Promise.all([
        tickersR.json(), volumeR.json(), lagOverallR.json(), leaderboardR.json(), convergenceR.json(),
      ]);

      if (tickersD.success) setTopTickers(tickersD.data.rows || []);
      if (volumeD.success) setTopVolume(volumeD.data.rows || []);
      if (lagOverallD.success) setFilingLagOverall(lagOverallD.data.rows[0] || null);
      if (leaderboardD.success) setLeaderboard(leaderboardD.data.rows || []);
      if (convergenceD.success) setConvergence(convergenceD.data.rows || []);

      if (![tickersD, volumeD, lagOverallD, leaderboardD, convergenceD].every(d => d.success)) {
        setError('Some insights could not be loaded — showing what did load successfully.');
      }
    } catch (e) {
      setError('Could not reach the server.');
    }
    setLoading(false);
  }, [dateWindow, status, rowLimit, convergenceDays, token]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <ConFilterBar
        showWindow dateWindow={dateWindow} onWindowChange={e => setDateWindow(e.target.value)}
        showStatus status={status} onStatusChange={e => setStatus(e.target.value)}
        showPageSize pageSize={rowLimit} onPageSizeChange={e => setRowLimit(parseInt(e.target.value, 10))}
      />

      {error && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid #ef4444', borderRadius: '8px', padding: '0.75rem 1rem', color: '#ef4444', marginBottom: '1rem', fontSize: '0.85rem' }}>{error}</div>}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>Loading...</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1rem' }}>

          {/* ── Top traded tickers ── */}
          <Card>
            <p style={{ margin: '0 0 0.75rem', color: '#94a3b8', fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <TrendingUp size={13} /> Top traded tickers
            </p>
            {topTickers.length === 0 ? (
              <p style={{ color: '#64748b', fontSize: '0.82rem', margin: 0 }}>No data for this window.</p>
            ) : topTickers.map((t, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0', fontSize: '0.82rem', color: '#e2e8f0' }}>
                <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{t.TICKER}</span>
                <span style={{ color: '#64748b' }}>{t.TRADECOUNT} trades · {fmtUSD(t.ESTAMOUNT)}</span>
              </div>
            ))}
          </Card>

          {/* ── Top buy/sell volume ── */}
          <Card>
            <p style={{ margin: '0 0 0.75rem', color: '#94a3b8', fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase' }}>Top buy / sell volume</p>
            {topVolume.length === 0 ? (
              <p style={{ color: '#64748b', fontSize: '0.82rem', margin: 0 }}>No data for this window.</p>
            ) : topVolume.map((t, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0', fontSize: '0.82rem', color: '#e2e8f0' }}>
                <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{t.TICKER}</span>
                <span style={{ color: t.TRANSACTIONTYPE === 'Sale' ? '#ef4444' : '#00ff88' }}>{t.TRANSACTIONTYPE} · {fmtUSD(t.ESTAMOUNT)}</span>
              </div>
            ))}
          </Card>

          {/* ── Filing lag ── */}
          <Card>
            <p style={{ margin: '0 0 0.5rem', color: '#94a3b8', fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase' }}>Filing lag</p>
            {filingLagOverall && (
              <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', fontSize: '0.85rem', color: '#e2e8f0', marginBottom: '0.75rem' }}>
                <span>Median: {filingLagOverall.MEDIANLAGDAYS != null ? `${filingLagOverall.MEDIANLAGDAYS}d` : '—'}</span>
                <span>Avg: {filingLagOverall.AVGLAGDAYS != null ? `${filingLagOverall.AVGLAGDAYS}d` : '—'}</span>
                <span>{filingLagOverall.TRADECOUNT || 0} trades</span>
              </div>
            )}
            <p style={{ margin: '0.5rem 0 0.4rem', color: '#64748b', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase' }}>Leaderboard — worst average lag</p>
            {leaderboard.length === 0 ? (
              <p style={{ color: '#64748b', fontSize: '0.82rem', margin: 0 }}>Not enough data (needs 3+ trades per member).</p>
            ) : leaderboard.map((m, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0', fontSize: '0.82rem', color: '#e2e8f0' }}>
                <span>{m.MEMBERNAME}</span>
                <span style={{ color: '#f59e0b' }}>{m.AVGLAGDAYS}d avg · {m.TRADECOUNT} trades</span>
              </div>
            ))}
          </Card>

          {/* ── Bipartisan Convergence — cluster context, NOT a signal ── */}
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <ArrowLeftRight size={13} /> Bipartisan convergence
              </p>
              {/* Deliberately a SEPARATE control from the shared window filter
                  above — this "window" means days-between-two-trades, not a
                  lookback period, and conflating the two would be confusing.
                  Unchanged from before this pass. */}
              <Select value={convergenceDays} onChange={e => setConvergenceDays(e.target.value)}
                options={[{ value: '7', label: 'Within 7 days' }, { value: '14', label: 'Within 14 days' }, { value: '30', label: 'Within 30 days' }]} />
            </div>
            <p style={{ margin: '0 0 0.6rem', color: '#64748b', fontSize: '0.72rem' }}>
              Cluster context, not a trading signal — members from both parties traded the same ticker within the selected window.
            </p>
            {convergence.length === 0 ? (
              <p style={{ color: '#64748b', fontSize: '0.82rem', margin: 0 }}>No cross-party convergence found for this window.</p>
            ) : convergence.map((c, i) => (
              <div key={i} style={{ padding: '0.4rem 0', fontSize: '0.8rem', color: '#e2e8f0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <div style={{ fontFamily: 'monospace', fontWeight: 700, marginBottom: '0.2rem' }}>{c.TICKER}</div>
                <div style={{ color: '#64748b', fontSize: '0.75rem' }}>
                  <span style={{ color: PARTY_COLORS[c.PARTYA] || '#94a3b8' }}>{c.MEMBERA} ({c.PARTYA})</span> {fmtDate(c.DATEA)}
                  {' · '}
                  <span style={{ color: PARTY_COLORS[c.PARTYB] || '#94a3b8' }}>{c.MEMBERB} ({c.PARTYB})</span> {fmtDate(c.DATEB)}
                </div>
              </div>
            ))}
          </Card>

        </div>
      )}
    </div>
  );
}
